export const assistantContentDomId = (messageId: string) =>
  `agent-assistant-content-${messageId}`;

const HEADING_TAG_PATTERN = /^h[1-6]$/;

const isBlockTag = (tag: string) =>
  tag === 'p' ||
  tag === 'blockquote' ||
  tag === 'table' ||
  tag === 'ul' ||
  tag === 'ol' ||
  HEADING_TAG_PATTERN.test(tag);

const nodeToText = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const el = node as Element;
  const tag = el.tagName.toLowerCase();

  if (tag === 'br') return '\n';

  if (tag === 'tr') {
    return Array.from(el.children)
      .map((cell) => nodeToText(cell).trim())
      .join('\t');
  }

  if (tag === 'table') {
    return Array.from(el.querySelectorAll('tr'))
      .map((row) => nodeToText(row))
      .join('\n');
  }

  if (tag === 'ul' || tag === 'ol') {
    return Array.from(el.children)
      .filter((child) => child.tagName.toLowerCase() === 'li')
      .map(
        (item, index) =>
          `${tag === 'ol' ? `${index + 1}.` : '-'} ${nodeToText(item).trim()}`,
      )
      .join('\n');
  }

  return Array.from(el.childNodes)
    .map((child) => nodeToText(child))
    .join('');
};

const collectBlocks = (node: Node, blocks: string[]): void => {
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as Element;
  const tag = el.tagName.toLowerCase();

  if (isBlockTag(tag)) {
    const text = nodeToText(el).trim();
    if (text) blocks.push(text);
    return;
  }

  el.childNodes.forEach((child) => collectBlocks(child, blocks));
};

const elementToPlainText = (root: Element): string => {
  const blocks: string[] = [];
  collectBlocks(root, blocks);
  return blocks.join('\n\n');
};

export type AssistantClipboardContent = { html: string; text: string };

export const buildAssistantClipboardContent = (
  messageIds: string[],
): AssistantClipboardContent | null => {
  const elements = messageIds
    .map((id) => document.getElementById(assistantContentDomId(id)))
    .filter((el): el is HTMLElement => Boolean(el));

  if (elements.length === 0) return null;

  return {
    html: elements.map((el) => el.innerHTML).join(''),
    text: elements.map((el) => elementToPlainText(el)).join('\n\n'),
  };
};

/**
 * Selects the given nodes and lets the browser run its own copy path, which is
 * what makes this identical to a manual select-and-copy: when the browser
 * serialises a Selection it inlines each node's *computed* styles, so the paste
 * keeps the colours, fonts and table metrics shown on screen. Handing the
 * clipboard `innerHTML` instead loses all of that — the emotion class names
 * travel with the markup but the stylesheet does not, so the paste target
 * applies its own defaults for `<code>`, `<table>`, and friends.
 *
 * `execCommand` is deprecated but it is the only API that produces a
 * native-selection payload, and it must run synchronously inside the user
 * gesture that triggered the copy — never `await` anything before calling this.
 *
 * The user's own selection is saved and restored within the same task, so no
 * paint happens in between and the highlight never visibly flickers.
 */
const copyViaNativeSelection = (elements: HTMLElement[]): boolean => {
  const selection = window.getSelection();
  if (!selection || typeof document.execCommand !== 'function') return false;

  const previousRanges = Array.from({ length: selection.rangeCount }, (_, i) =>
    selection.getRangeAt(i),
  );

  const range = document.createRange();
  range.setStartBefore(elements[0]);
  range.setEndAfter(elements[elements.length - 1]);

  selection.removeAllRanges();
  selection.addRange(range);

  let copied = false;
  try {
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  } finally {
    selection.removeAllRanges();
    previousRanges.forEach((previousRange) =>
      selection.addRange(previousRange),
    );
  }

  return copied;
};

export const copyRenderedSelection = async (
  messageIds: string[],
): Promise<boolean> => {
  const elements = messageIds
    .map((id) => document.getElementById(assistantContentDomId(id)))
    .filter((el): el is HTMLElement => Boolean(el));
  if (elements.length === 0) return false;

  if (copyViaNativeSelection(elements)) return true;

  // Only reached where `execCommand` is unavailable or refused. This payload is
  // hand-serialised, so pasted styling will not match the page as closely.
  const rendered = buildAssistantClipboardContent(messageIds);
  const text = rendered?.text ?? '';

  try {
    if (rendered && typeof ClipboardItem !== 'undefined') {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([rendered.html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        }),
      ]);
    } else {
      await navigator.clipboard.writeText(text);
    }
    return true;
  } catch {
    return false;
  }
};
